<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    // Filter out disabled bots for selection (but keep currently active one if disabled)
    availableBots() {
      const activeBot = this.activeAgentBot;
      return this.agentBots.filter(bot => 
        !bot.disabled_by_plan || (activeBot && bot.id === activeBot.id)
      );
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    selectedBot() {
      return this.agentBots.find(bot => bot.id === this.selectedAgentBotId);
    },
    // Check if the currently active bot is disabled
    isActiveBotDisabled() {
      return this.activeAgentBot?.disabled_by_plan || false;
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE')
        );
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div>
          <!-- Warning if currently active bot is disabled -->
          <div
            v-if="isActiveBotDisabled"
            class="mb-4 flex items-center gap-2 p-3 rounded-lg bg-ruby-50 dark:bg-ruby-900/20 border border-ruby-200 dark:border-ruby-800"
          >
            <span class="i-lucide-alert-triangle size-5 text-ruby-600 dark:text-ruby-400" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-ruby-700 dark:text-ruby-300">
                Bot Atual Desabilitado
              </span>
              <span class="text-xs text-ruby-600 dark:text-ruby-400">
                O bot "{{ activeAgentBot?.name }}" foi desabilitado por exceder o limite do plano. Selecione outro bot ou faça upgrade.
              </span>
            </div>
          </div>

          <label>
            <select v-model="selectedAgentBotId">
              <option value="" disabled selected>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agentBot in availableBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                {{ agentBot.name }}{{ agentBot.disabled_by_plan ? ' (Desabilitado - Atual)' : '' }}
              </option>
            </select>
          </label>

          <div class="button-container space-x-2 mt-4">
            <NextButton
              type="submit"
              :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :is-loading="uiFlags.isSettingAgentBot"
              :disabled="selectedBot?.disabled_by_plan && selectedAgentBotId !== activeAgentBot?.id"
            />
            <NextButton
              type="button"
              :disabled="!selectedAgentBotId"
              :is-loading="uiFlags.isDisconnecting"
              faded
              ruby
              @click="disconnectBot"
            >
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
            </NextButton>
          </div>
        </div>
      </SettingsSection>
    </form>
  </div>
</template>
